import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Upload as UploadIcon, Plane, MapPin, Image, Loader2, X, ArrowRight, Link as LinkIcon, Sun, CheckCircle, XCircle, Sparkles } from 'lucide-react';

const AIRLINES = [
  'American Airlines', 'Delta Air Lines', 'United Airlines', 'Southwest Airlines',
  'JetBlue Airways', 'Alaska Airlines', 'Spirit Airlines', 'Frontier Airlines',
  'British Airways', 'Lufthansa', 'Air France', 'Emirates', 'Qatar Airways',
  'Singapore Airlines', 'Cathay Pacific', 'Japan Airlines', 'ANA', 'Korean Air',
  'Virgin Atlantic', 'KLM', 'Turkish Airlines', 'Etihad Airways', 'Qantas',
  'Air Canada', 'WestJet', 'Ryanair', 'EasyJet', 'Norwegian', 'Other'
];

const AIRCRAFT_TYPES = [
  'Boeing 737', 'Boeing 747', 'Boeing 757', 'Boeing 767', 'Boeing 777', 'Boeing 787 Dreamliner',
  'Airbus A320', 'Airbus A321', 'Airbus A330', 'Airbus A350', 'Airbus A380',
  'Embraer E175', 'Embraer E190', 'Bombardier CRJ', 'ATR 72', 'Cessna', 'Other'
];

export default function Upload() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadMethod, setUploadMethod] = useState('file');
  const [goldenHourResult, setGoldenHourResult] = useState(null);
  const [checkingGoldenHour, setCheckingGoldenHour] = useState(false);
  const [formData, setFormData] = useState({
    image_url: '',
    caption: '',
    airline: '',
    aircraft_type: '',
    departure: '',
    arrival: '',
    seat: '',
    latitude: '',
    longitude: ''
  });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload and validate with AI
    setUploading(true);
    setValidating(true);
    setValidationResult(null);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Use AI validation endpoint
      const response = await axios.post(`${API}/validate-image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setValidationResult(response.data);
      
      if (response.data.valid) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success('Image verified as airplane window view!');
      } else {
        toast.error(response.data.reason || 'This doesn\'t appear to be an airplane window view');
        setImagePreview('');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      setImagePreview('');
    } finally {
      setUploading(false);
      setValidating(false);
    }
  };

  const handleImageUrl = (url) => {
    setFormData({ ...formData, image_url: url });
    setImagePreview(url);
    setValidationResult(null); // URL uploads bypass AI validation for now
  };

  const checkGoldenHour = async () => {
    if (!formData.departure || !formData.arrival) {
      toast.error('Please enter departure and arrival airports');
      return;
    }

    setCheckingGoldenHour(true);
    try {
      const response = await axios.post(`${API}/golden-hour`, {
        departure: formData.departure,
        arrival: formData.arrival
      }, { withCredentials: true });
      
      setGoldenHourResult(response.data);
    } catch (error) {
      console.error('Golden hour error:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate golden hour');
    } finally {
      setCheckingGoldenHour(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error('Please add an image');
      return;
    }

    setLoading(true);

    try {
      const postData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      await axios.post(`${API}/posts`, postData, { withCredentials: true });
      
      toast.success('Window view shared successfully!');
      navigate('/feed');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to share post');
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setImagePreview('');
    setFormData({ ...formData, image_url: '' });
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="upload-page">
      <div className="mb-8">
        <h1 
          className="text-3xl font-bold text-white mb-2"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Share Your View
        </h1>
        <p className="text-gray-400">Upload a window seat photo and tag your flight details</p>
      </div>

      {/* AI Validation Notice */}
      <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <span className="text-purple-300 font-medium">AI-Powered Validation</span>
        </div>
        <p className="text-sm text-gray-400 mt-1">
          Our AI will verify that your photo is an authentic airplane window view before posting.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Image Section */}
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-purple-400" />
            Photo
          </h2>

          <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="mb-4">
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="file" className="data-[state=active]:bg-purple-500/20">
                <UploadIcon className="w-4 h-4 mr-2" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="url" className="data-[state=active]:bg-purple-500/20">
                <LinkIcon className="w-4 h-4 mr-2" />
                Image URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div 
                className={`upload-preview mb-4 cursor-pointer ${imagePreview ? 'has-image' : ''}`}
                onClick={() => !imagePreview && fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    {(uploading || validating) && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-2" />
                        <span className="text-white text-sm">
                          {validating ? 'AI is verifying your image...' : 'Uploading...'}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); clearImage(); }}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                    {/* Validation Result Badge */}
                    {validationResult && !validating && (
                      <div className={`absolute bottom-3 left-3 right-3 p-3 rounded-lg ${
                        validationResult.valid 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : 'bg-red-500/20 border border-red-500/30'
                      }`}>
                        <div className="flex items-center gap-2">
                          {validationResult.valid ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                          <span className={`text-sm font-medium ${
                            validationResult.valid ? 'text-green-300' : 'text-red-300'
                          }`}>
                            {validationResult.valid ? 'Verified airplane window view' : 'Not a valid window view'}
                          </span>
                        </div>
                        {validationResult.reason && (
                          <p className="text-xs text-gray-400 mt-1 ml-7">{validationResult.reason}</p>
                        )}
                        {validationResult.confidence > 0 && (
                          <p className="text-xs text-gray-500 mt-1 ml-7">
                            Confidence: {validationResult.confidence}%
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 min-h-[200px]">
                    <UploadIcon className="w-12 h-12 mb-4" />
                    <p className="text-sm">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-600 mt-1">PNG, JPG up to 5MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </TabsContent>

            <TabsContent value="url" className="mt-4">
              <div className={`upload-preview mb-4 ${imagePreview ? 'has-image' : ''}`}>
                {imagePreview ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={() => {
                        setImagePreview('');
                        toast.error('Failed to load image');
                      }}
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 min-h-[200px]">
                    <LinkIcon className="w-12 h-12 mb-4" />
                    <p className="text-sm">Enter an image URL below</p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url" className="text-gray-300">Image URL</Label>
                <Input
                  id="image_url"
                  type="url"
                  placeholder="https://example.com/your-window-view.jpg"
                  value={uploadMethod === 'url' ? formData.image_url : ''}
                  onChange={(e) => handleImageUrl(e.target.value)}
                  className="input-ventana"
                  data-testid="image-url-input"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Caption Section */}
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Caption</h2>
          <Textarea
            placeholder="Describe your view... What made this moment special?"
            value={formData.caption}
            onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
            className="input-ventana min-h-[100px] resize-none"
            data-testid="caption-input"
          />
        </div>

        {/* Flight Details Section */}
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Plane className="w-5 h-5 text-purple-400" />
            Flight Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Airline</Label>
              <Select 
                value={formData.airline} 
                onValueChange={(value) => setFormData({ ...formData, airline: value })}
              >
                <SelectTrigger className="input-ventana" data-testid="airline-select">
                  <SelectValue placeholder="Select airline" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F16] border-white/10 max-h-[300px]">
                  {AIRLINES.map(airline => (
                    <SelectItem key={airline} value={airline}>{airline}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Aircraft Type</Label>
              <Select 
                value={formData.aircraft_type} 
                onValueChange={(value) => setFormData({ ...formData, aircraft_type: value })}
              >
                <SelectTrigger className="input-ventana" data-testid="aircraft-select">
                  <SelectValue placeholder="Select aircraft" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F16] border-white/10 max-h-[300px]">
                  {AIRCRAFT_TYPES.map(aircraft => (
                    <SelectItem key={aircraft} value={aircraft}>{aircraft}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="departure" className="text-gray-300">Departure (Airport Code)</Label>
              <Input
                id="departure"
                placeholder="e.g., JFK"
                value={formData.departure}
                onChange={(e) => setFormData({ ...formData, departure: e.target.value.toUpperCase() })}
                className="input-ventana"
                maxLength={4}
                data-testid="departure-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="arrival" className="text-gray-300">Arrival (Airport Code)</Label>
              <Input
                id="arrival"
                placeholder="e.g., LAX"
                value={formData.arrival}
                onChange={(e) => setFormData({ ...formData, arrival: e.target.value.toUpperCase() })}
                className="input-ventana"
                maxLength={4}
                data-testid="arrival-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seat" className="text-gray-300">Seat</Label>
              <Input
                id="seat"
                placeholder="e.g., 22A"
                value={formData.seat}
                onChange={(e) => setFormData({ ...formData, seat: e.target.value.toUpperCase() })}
                className="input-ventana"
                data-testid="seat-input"
              />
            </div>
          </div>

          {/* Golden Hour Checker */}
          {formData.departure && formData.arrival && (
            <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sun className="w-5 h-5 text-orange-400" />
                  <span className="font-medium text-white">Golden Hour Tracker</span>
                </div>
                <Button
                  type="button"
                  onClick={checkGoldenHour}
                  disabled={checkingGoldenHour}
                  size="sm"
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-300"
                >
                  {checkingGoldenHour ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Check Best Seat'
                  )}
                </Button>
              </div>
              
              {goldenHourResult && (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Flight:</span>
                    <span className="text-white font-mono">
                      {goldenHourResult.departure} → {goldenHourResult.arrival}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Recommended Side:</span>
                    <span className={`font-semibold ${
                      goldenHourResult.best_side === 'left' ? 'text-blue-400' :
                      goldenHourResult.best_side === 'right' ? 'text-green-400' :
                      'text-gray-400'
                    }`}>
                      {goldenHourResult.best_side === 'left' ? '← Left (A seats)' :
                       goldenHourResult.best_side === 'right' ? 'Right (F/K seats) →' :
                       'Either side'}
                    </span>
                  </div>
                  <p className="text-gray-300 pt-2 border-t border-white/10">
                    {goldenHourResult.reason}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Location Section */}
        <div className="glass-card rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-purple-400" />
            Location (Optional)
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Add coordinates to show your photo on the interactive globe
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="latitude" className="text-gray-300">Latitude</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                placeholder="e.g., 40.7128"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                className="input-ventana"
                data-testid="latitude-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="longitude" className="text-gray-300">Longitude</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                placeholder="e.g., -74.0060"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                className="input-ventana"
                data-testid="longitude-input"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || !formData.image_url || uploading}
          className="btn-primary-gradient rounded-full w-full py-6 text-lg font-medium"
          data-testid="submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sharing...
            </>
          ) : (
            <>
              Share Your View
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
      </form>
    </div>
  );
}

export { Upload };
